import * as React from 'react';
import { TIMELINE_SPANS, ZOOM_SPANS } from './timeline.utils';

const NOW_REFRESH_MS = 15 * 1000;

export type TimelineWindowInitial =
  | { live: true; span: number }
  /** A fixed window, e.g. the alarm's own; `pinned` is the moment the map opens on. */
  | { live: false; start: number; end: number; pinned?: number };

type WindowState = {
  span: number;
  end: number;
  live: boolean;
  pinned: number | null;
  isInitial: boolean;
};

function initialState(initial: TimelineWindowInitial): WindowState {
  if (initial.live) {
    return { span: initial.span, end: Date.now(), live: true, pinned: null, isInitial: true };
  }
  return {
    span: initial.end - initial.start,
    end: initial.end,
    live: false,
    pinned: initial.pinned ?? null,
    isInitial: true,
  };
}

export function useTimelineWindow(initial: TimelineWindowInitial) {
  const [state, setState] = React.useState(() => initialState(initial));
  const [now, setNow] = React.useState(Date.now);

  React.useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), NOW_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  const end = state.live ? now : Math.min(state.end, now);
  const start = end - state.span;

  const setSpan = (span: number) =>
    setState((current) => {
      if (current.live) return { ...current, span, isInitial: false };
      const center = current.pinned ?? current.end - current.span / 2;
      return { ...current, span, end: Math.min(Date.now(), center + span / 2), isInitial: false };
    });

  const smaller = [...ZOOM_SPANS].reverse().find((span) => span < state.span);
  const larger = ZOOM_SPANS.find((span) => span > state.span);

  return {
    start,
    end,
    now,
    span: state.span,
    live: state.live,
    pinned: state.pinned,
    isInitial: state.isInitial,
    canZoomIn: smaller !== undefined,
    canZoomOut: larger !== undefined,
    canShiftLater: end < now,
    setSpan,
    zoomIn: () => smaller !== undefined && setSpan(smaller),
    zoomOut: () => larger !== undefined && setSpan(larger),
    shift: (direction: -1 | 1) =>
      setState((current) => ({
        ...current,
        end: Math.min(Date.now(), end + (direction * current.span) / 2),
        live: false,
        isInitial: false,
      })),
    /** Shows a whole day, up to now when it is today. */
    showDay: (day: Date) => {
      const dayEnd = new Date(day);
      dayEnd.setHours(24, 0, 0, 0);
      setState({
        span: TIMELINE_SPANS[TIMELINE_SPANS.length - 1],
        end: Math.min(Date.now(), dayEnd.getTime()),
        live: false,
        pinned: null,
        isInitial: false,
      });
    },
    /** Zooms to a dragged stretch, widened around its middle when shorter than the closest zoom. */
    select: (from: number, to: number) => {
      const minSpan = ZOOM_SPANS[0];
      const maxSpan = ZOOM_SPANS[ZOOM_SPANS.length - 1];
      const span = Math.min(maxSpan, Math.max(minSpan, to - from));
      const selectedEnd = Math.min(Date.now(), (from + to) / 2 + span / 2);
      setState((current) => ({
        ...current,
        span,
        end: selectedEnd,
        live: false,
        pinned:
          current.pinned !== null && current.pinned >= selectedEnd - span && current.pinned <= selectedEnd
            ? current.pinned
            : null,
        isInitial: false,
      }));
    },
    pin: (time: number) => setState((current) => ({ ...current, pinned: time, live: false, end })),
    goLive: () => setState((current) => ({ ...current, live: true, pinned: null, isInitial: false })),
  };
}

export type TimelineWindow = ReturnType<typeof useTimelineWindow>;
